import type { PixelEvent } from '@/events';

import {
  BaseDestinationAdapter,
  DEFAULT_COMMERCE_EVENT_TYPES,
} from './BaseDestinationAdapter';
import type { DestinationResult } from '../types';

/**
 * In-memory destination for tests and playground inspection.
 * Stores transformed payloads — never mutates PixelEvent.
 */
export class MemoryDestination extends BaseDestinationAdapter {
  private readonly records: Array<{
    readonly at: number;
    readonly type: string;
    readonly payload: Readonly<Record<string, unknown>>;
  }> = [];

  constructor() {
    super({
      name: 'memory',
      version: '1.0.0',
      supportedTypes: DEFAULT_COMMERCE_EVENT_TYPES,
    });
  }

  async execute(context: {
    readonly event: PixelEvent;
    readonly signal: AbortSignal;
  }): Promise<DestinationResult> {
    const startedAt = Date.now();

    if (context.signal.aborted) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'Aborted',
      });
    }

    const payload = this.transform(context.event);
    this.records.push({
      at: Date.now(),
      type: context.event.type,
      payload,
    });

    return this.createResult('success', Date.now() - startedAt, {
      metadata: { recordCount: this.records.length },
    });
  }

  getRecords(): readonly {
    readonly at: number;
    readonly type: string;
    readonly payload: Readonly<Record<string, unknown>>;
  }[] {
    return [...this.records];
  }

  clear(): void {
    this.records.length = 0;
  }

  destroy(): void {
    this.clear();
  }

  private transform(event: PixelEvent): Record<string, unknown> {
    return {
      destination: 'memory',
      eventType: event.type,
      timestamp: event.timestamp,
      path: 'path' in event ? event.path : undefined,
      data: 'data' in event ? event.data : undefined,
    };
  }
}
